import {
  getExamLanguageContext,
  getExamTypesForLanguage,
  getLanguageRuntimeContext,
  normalizeExamType,
} from './languageContext';
import { getLanguages } from './config/examStructure';

export interface PlatformScopeInput {
  languageId?: string | null;
  examType?: string | null;
  skillId?: string | null;
}

export interface PlatformScope {
  languageId: string;
  languageName: string;
  languageNameEn: string;
  locale: string;
  ttsLocale: string;
  examType: string;
  examName: string;
  examFullName: string;
  skillId: string | null;
}

export interface LearningTargetInput {
  contentPack?: string | null;
  moduleId?: string | null;
  languageId?: string | null;
  examType?: string | null;
  skillId?: string | null;
}

export interface AITaskScopeInput {
  languageId?: unknown;
  examType?: unknown;
  section?: unknown;
  skillId?: unknown;
}

export interface EntitlementScopeInput {
  examTypes?: Array<string | null | undefined> | null;
  languageIds?: Array<string | null | undefined> | null;
}

function normalizeOptional(value: string | null | undefined): string | null {
  const normalized = String(value ?? '')
    .trim()
    .toLowerCase();
  return normalized || null;
}

function normalizeUniqueList(
  values: Array<string | null | undefined> | null | undefined
): string[] {
  return Array.from(
    new Set((values ?? []).map((value) => normalizeOptional(value)).filter(Boolean) as string[])
  ).sort();
}

function getScopeOrder(scope: PlatformScope): [number, number, string] {
  const languageIndex = getLanguages().findIndex((language) => language.id === scope.languageId);
  const language = getLanguages()[languageIndex];
  const examIndex = language?.exams.findIndex((exam) => exam.id === scope.examType) ?? -1;

  return [
    languageIndex >= 0 ? languageIndex : Number.MAX_SAFE_INTEGER,
    examIndex >= 0 ? examIndex : Number.MAX_SAFE_INTEGER,
    platformScopeKey(scope) ?? '',
  ];
}

export function buildPlatformScope(input: PlatformScopeInput): PlatformScope | null {
  const examType = normalizeExamType(input.examType);
  if (!examType) return null;

  const examContext = getExamLanguageContext(examType);
  if (!examContext) return null;

  const explicitLanguageId = normalizeOptional(input.languageId);
  if (explicitLanguageId && explicitLanguageId !== examContext.languageId) {
    return null;
  }

  return {
    ...examContext,
    skillId: normalizeOptional(input.skillId),
  };
}

export function getPlatformScopeIssues(input: PlatformScopeInput): string[] {
  const examType = normalizeExamType(input.examType);
  const languageId = normalizeOptional(input.languageId);
  const issues: string[] = [];

  if (!examType) {
    issues.push('examType is required');
    return issues;
  }

  const examContext = getExamLanguageContext(examType);
  if (!examContext) {
    issues.push(`exam ${examType} is not registered`);
    return issues;
  }

  if (languageId && !getLanguageRuntimeContext(languageId)) {
    issues.push(`language ${languageId} is not registered`);
  }

  if (languageId && languageId !== examContext.languageId) {
    issues.push(
      `exam ${examType} belongs to language ${examContext.languageId}, not ${languageId}`
    );
  }

  return issues;
}

export function scopeFromLearningTarget(input: LearningTargetInput): PlatformScope | null {
  return buildPlatformScope({
    languageId: input.languageId,
    examType: input.examType || input.contentPack,
    skillId: input.skillId || input.moduleId,
  });
}

export function scopeFromAITaskPayload(input: AITaskScopeInput): PlatformScope | null {
  return buildPlatformScope({
    languageId: typeof input.languageId === 'string' ? input.languageId : null,
    examType: typeof input.examType === 'string' ? input.examType : null,
    skillId:
      typeof input.skillId === 'string'
        ? input.skillId
        : typeof input.section === 'string'
          ? input.section
          : null,
  });
}

export function platformScopeKey(scope: PlatformScope | null | undefined): string | null {
  if (!scope) return null;
  return [scope.languageId, scope.examType, scope.skillId || 'all'].join(':');
}

export function buildEntitlementPlatformScopes(input: EntitlementScopeInput): PlatformScope[] {
  const scopeByKey = new Map<string, PlatformScope>();
  const addScope = (scope: PlatformScope | null) => {
    const key = platformScopeKey(scope);
    if (scope && key && !scopeByKey.has(key)) {
      scopeByKey.set(key, scope);
    }
  };

  for (const examType of normalizeUniqueList(input.examTypes)) {
    addScope(buildPlatformScope({ examType }));
  }

  for (const languageId of normalizeUniqueList(input.languageIds)) {
    for (const examType of getExamTypesForLanguage(languageId)) {
      addScope(buildPlatformScope({ languageId, examType }));
    }
  }

  return Array.from(scopeByKey.values()).sort((a, b) => {
    const [aLanguage, aExam, aKey] = getScopeOrder(a);
    const [bLanguage, bExam, bKey] = getScopeOrder(b);
    if (aLanguage !== bLanguage) return aLanguage - bLanguage;
    if (aExam !== bExam) return aExam - bExam;
    return aKey.localeCompare(bKey);
  });
}

export function getEntitlementScopeIssues(input: EntitlementScopeInput): string[] {
  const examTypes = normalizeUniqueList(input.examTypes);
  const languageIds = normalizeUniqueList(input.languageIds);
  const issues: string[] = [];

  if (examTypes.length === 0 && languageIds.length === 0) {
    issues.push('entitlement scope is empty');
    return issues;
  }

  for (const examType of examTypes) {
    if (!getExamLanguageContext(examType)) {
      issues.push(`exam ${examType} is not registered`);
    }
  }

  for (const languageId of languageIds) {
    if (!getLanguageRuntimeContext(languageId)) {
      issues.push(`language ${languageId} is not registered`);
    }
  }

  if (buildEntitlementPlatformScopes(input).length === 0) {
    issues.push('entitlement scope has no usable platform scopes');
  }

  return issues;
}
